/**
 * Event rebate requests (flag `event_rebate_requests`): a store asks a brand's
 * vendor to approve an event or anniversary rebate and the vendor decides.
 * See `/store/rebate-requests` and `/vendor/rebate-requests` on the API.
 */
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';

export type RebateRequestStatus = 'pending' | 'approved' | 'denied' | 'withdrawn' | 'expired';

export interface RebateEventRequest {
  id: string;
  storeOrganizationId: string;
  vendorOrganizationId: string;
  brandId: string;
  brandName: string;
  kind: 'event' | 'anniversary';
  eventName: string;
  termsKind: 'instant_rebate' | 'trade_credit';
  amount: number;
  productIds: string[] | null;
  startAt: string;
  endAt: string;
  scope: { kind: 'all' | 'location' | 'integration' | 'zone'; locationId?: string | null; integrationId?: string | null };
  requestNote: string | null;
  status: RebateRequestStatus;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  rebateProgramId: string | null;
  createdAt: string;
  /** Vendor view only. */
  storeOrganizationName?: string | null;
}

export interface CreateRebateRequestInput {
  brand: string;
  kind: 'event' | 'anniversary';
  eventName: string;
  termsKind: 'instant_rebate' | 'trade_credit';
  amount: number;
  startAt: string;
  endAt: string;
  scope?: { kind: 'all' } | { kind: 'location'; locationId: string };
  note?: string;
}

async function call<T>(path: string, init?: { method?: string; body?: unknown }): Promise<T> {
  const res = await makeApiCall(path, {
    method: init?.method || 'GET',
    headers: await getAuthHeaders(),
    ...(init?.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload?.error?.message || payload?.message || 'Request failed');
  return payload?.data as T;
}

export const listStoreRebateRequests = (): Promise<RebateEventRequest[]> => call('/store/rebate-requests');
export const createStoreRebateRequest = (input: CreateRebateRequestInput): Promise<RebateEventRequest> =>
  call('/store/rebate-requests', { method: 'POST', body: input });
export const withdrawStoreRebateRequest = (id: string): Promise<RebateEventRequest> =>
  call(`/store/rebate-requests/${id}/withdraw`, { method: 'POST', body: {} });

export const VENDOR_REBATE_REQUESTS_QUERY_KEY = ['vendor-rebate-requests'] as const;
export const listVendorRebateRequests = (): Promise<RebateEventRequest[]> => call('/vendor/rebate-requests');
export const decideVendorRebateRequest = (
  id: string,
  action: 'approve' | 'deny',
  note?: string,
): Promise<RebateEventRequest> => call(`/vendor/rebate-requests/${id}/${action}`, { method: 'POST', body: { note: note || undefined } });
